import React, { useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { Link, Redirect, Route, Switch, useLocation } from "wouter";
import { ChevronDown, ChevronRight, Home as HomeIcon, LogOut, ScrollText, Settings as SettingsIcon, Plus, FolderClosed } from "lucide-react";
import { checkAuth, logoutNow, useAuth, useQuery, useStatus } from "./lib";
import { cx, Dot, KindIcon, ToastHost } from "./ui";
import { Home } from "./pages/home";
import { ProjectPage } from "./pages/project";
import { TargetPage } from "./pages/target";
import { EvalRunPage } from "./pages/evalrun";
import { TastePage } from "./pages/taste";
import { LogsPage } from "./pages/logs";
import { SettingsPage } from "./pages/settings";
import { NewProjectModal } from "./pages/modals";
import { InvitePage, LoginPage } from "./pages/auth";

function Sidebar() {
  const { data: projects } = useQuery<any[]>("/api/projects");
  const [loc] = useLocation();
  const [closed, setClosed] = useState<Record<string, boolean>>({});
  const [newProject, setNewProject] = useState(false);
  const status = useStatus();
  const auth = useAuth();
  const who = auth.user?.name ?? "local";
  const provOk = status ? Object.entries(status.providers).filter(([k]) => k !== "fake") : [];
  const bad = provOk.filter(([, v]) => !v.ok || v.lastError).length;
  return (
    <aside className="sidebar">
      <Link href="/" className="brand">
        <span className="brand-dot" />
        ATELIÊ
      </Link>
      <div className="side-scroll">
        <Link href="/" className={cx("side-link", loc === "/" && "active")}>
          <HomeIcon size={15} />
          <span className="name">Início</span>
        </Link>
        <Link href="/usage" className={cx("side-link", (loc === "/usage" || loc === "/logs") && "active")}>
          <ScrollText size={15} />
          <span className="name">Usage</span>
        </Link>
        <div className="side-section">
          <span className="label">Projetos</span>
          <button className="btn btn-ghost btn-xs btn-icon" title="Novo projeto" onClick={() => setNewProject(true)}>
            <Plus size={13} />
          </button>
        </div>
        {(projects ?? []).map((p) => {
          const open = !closed[p.slug];
          const base = `/p/${p.slug}`;
          const projActive = loc === base || /^\/p\/[^/]+\/(e|d|logs|usage|avaliacoes)(\/|$)/.test(loc) && loc.startsWith(base + "/");
          return (
            <div key={p.slug} style={{ marginBottom: 4 }}>
              <div className={cx("side-link side-project", projActive && "active")}>
                <span onClick={() => setClosed({ ...closed, [p.slug]: open })} style={{ display: "grid", placeItems: "center", cursor: "pointer" }}>
                  {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </span>
                <Link href={base} className="name">{p.name}</Link>
              </div>
              {open && (
                <div className="side-children">
                  {p.targets.map((t: any) => {
                    const href = `${base}/t/${t.slug}`;
                    return (
                      <Link key={t.slug} href={href} className={cx("side-link", (loc === href || loc.startsWith(href + "/")) && "active")}>
                        <KindIcon kind={t.kind} />
                        <span className="name">{t.name}</span>
                        {t.channels?.production && <Dot tone="green" />}
                      </Link>
                    );
                  })}
                  {!p.targets.length && <div className="side-link faint">vazio</div>}
                </div>
              )}
            </div>
          );
        })}
        {projects && !projects.length && (
          <div className="side-link faint" onClick={() => setNewProject(true)}>
            <FolderClosed size={15} />
            criar o primeiro projeto
          </div>
        )}
      </div>
      <div className="side-foot">
        <span className="avatar">{who.slice(0, 1).toUpperCase()}</span>
        <span className="grow ellipsis" title={auth.user?.email}>{who}</span>
        {auth.status === "in" && (
          <button className="btn btn-ghost btn-xs btn-icon" title="Sair" onClick={logoutNow}>
            <LogOut size={14} />
          </button>
        )}
        <Link href="/settings" className="row" title={bad ? `${bad} provedor(es) com problema` : "Provedores ok"}>
          <Dot tone={bad ? "amber" : "green"} />
          <SettingsIcon size={15} className="faint" />
        </Link>
      </div>
      <NewProjectModal open={newProject} onClose={() => setNewProject(false)} />
    </aside>
  );
}

function App() {
  const auth = useAuth();
  const [loc] = useLocation();
  useEffect(() => void checkAuth(), []);
  const invite = /^\/convite\/([^/]+)$/.exec(loc);
  if (invite) return <><InvitePage token={invite[1]} /><ToastHost /></>;
  // O feed do gosto ocupa a tela toda (dá para votar no celular).
  const taste = /^\/p\/([^/]+)\/g\/([^/]+)$/.exec(loc);
  if (taste && auth.status !== "checking" && auth.status !== "login") return <><TastePage key={taste[2]} p={taste[1]} id={taste[2]} /><ToastHost /></>;
  if (auth.status === "checking") return <div className="auth-screen" />;
  if (auth.status === "login") return <><LoginPage /><ToastHost /></>;
  return (
    <div className="app">
      <Sidebar />
      <main className="main">
        <Switch>
          <Route path="/" component={Home} />
          <Route path="/usage" component={LogsPage} />
          <Route path="/logs" component={LogsPage} />
          <Route path="/settings" component={SettingsPage} />
          <Route path="/p/:p/t/:t/:tab?">{(params) => <TargetPage key={`${params.p}/${params.t}`} p={params.p} t={params.t} tab={params.tab ?? "editor"} />}</Route>
          <Route path="/p/:p/e/:id">{(params) => <EvalRunPage key={params.id} p={params.p} id={params.id} />}</Route>
          {/* Endereço antigo dos datasets do projeto; os casos agora ficam em cada alvo. */}
          <Route path="/p/:p/d/:d">{(params) => <Redirect to={"/p/" + params.p} />}</Route>
          <Route path="/p/:p/:tab?">{(params) => <ProjectPage key={params.p} p={params.p} tab={params.tab ?? "overview"} />}</Route>
          <Route>
            <div className="page">Página não encontrada.</div>
          </Route>
        </Switch>
      </main>
      <ToastHost />
    </div>
  );
}

createRoot(document.getElementById("root")!).render(<App />);
